import { FC, useCallback, useMemo, useState } from 'react';
import { useEventListener } from '@material-bank/ds-utils';
import { Link } from '@material-bank/mb-uikit/components/Link';
import { Stack } from '@material-bank/mb-uikit/components/Stack';
import { Text } from '@material-bank/mb-uikit/components/Typography';
import {
  Form,
  FormConsumer,
} from '@material-bank/mb-uikit/shared/components/Form';
import { CustomEventName } from '@mb/lib/customEvents';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { BoxProps } from 'theme-ui';

import { EmailField } from './EmailField';
import { PasswordField } from './PasswordField';
import { SignInFormErrorType, SignInFormValues } from './signInForm';
import * as styles from './styles';

import {
  AuthButton,
  AuthFormContainer,
  AuthPageContent,
  useResendVerificationEmailMutation,
  useSignInMutation,
} from '../../../../../modules/auth';
import { PageUrl } from '../../../../../shared/config';
import { translations } from '../../../i18n';
import {
  getSignInAnalyticsUserData,
  sendSignInAnalyticsEvent,
} from '../../../lib/analytics';

export interface SignInViewProps extends BoxProps {}

export const SignInView: FC<SignInViewProps> = (props) => {
  const { ...boxProps } = props;

  const form = useForm<SignInFormValues>({
    defaultValues: { email: '', password: '' },
  });
  const [isCustomerDataLoading, setCustomerDataLoading] = useState(false);

  const searchParams = useMemo(() => {
    return new URLSearchParams(window.location.search);
  }, []);

  const {
    mutate: resendVerificationEmail,
    isLoading: isVerificationEmailResending,
  } = useResendVerificationEmailMutation();
  const { data: signInResult, mutateAsync: signInAsync } = useSignInMutation({
    onSuccessResult: (result) => {
      setCustomerDataLoading(true);
      sendSignInAnalyticsEvent({
        user: getSignInAnalyticsUserData(result.customer),
      });
    },
    onFailedResult: (result) => {
      if (result.message === "We didn't recognize this email or password. ") {
        form.setError('email', {
          type: SignInFormErrorType.INCORRECT_EMAIL_OR_PASSWORD,
        });
        form.setError('password', {
          type: SignInFormErrorType.INCORRECT_EMAIL_OR_PASSWORD,
        });
      } else if (result.message && /<\/?[a-z][\s\S]*>/i.test(result.message)) {
        window.dispatchEvent(
          new CustomEvent('modal', {
            detail: {
              open: true,
              message: result.message,
              form: false,
              autoWidth: true,
            },
          }),
        );
      } else {
        form.setError('email', { message: result.message });
      }
    },
  });

  const onPrivateContentLoadedEvent = useCallback(() => {
    setCustomerDataLoading(false);

    if (!signInResult || signInResult.status !== 'success') {
      return;
    }

    const callbackUrl = searchParams.get('callbackUrl');

    window.location.href = signInResult.redirect_url || callbackUrl || '/';
  }, [searchParams, signInResult]);

  useEventListener(
    window,
    CustomEventName.PRIVATE_CONTENT_LOADED,
    onPrivateContentLoadedEvent,
  );

  const onResendConfirmationEmailEvent = useCallback(() => {
    const emailValue = form.getValues('email');

    resendVerificationEmail({ email: emailValue });
  }, [form, resendVerificationEmail]);

  useEventListener(
    window,
    'resend-confirmation-email',
    onResendConfirmationEmailEvent,
  );

  const onFormSubmit = useCallback(
    async (values: SignInFormValues) => {
      await signInAsync({
        username: values.email,
        password: values.password,
        redirect: searchParams.get('redirect') || undefined,
      });
    },
    [searchParams, signInAsync],
  );

  return (
    <AuthPageContent
      footer={
        <Link href={PageUrl.RESET_PASSWORD} withUnderline inherit>
          <FormattedMessage {...translations.signInView.forgotPassword} />
        </Link>
      }
      {...boxProps}
    >
      <AuthFormContainer>
        <Form<SignInFormValues> form={form} onSubmit={onFormSubmit}>
          <Stack gap={[4, null, 6]}>
            <EmailField />
            <PasswordField />
            <Text variant="label1" sx={styles.text}>
              <FormattedMessage
                {...translations.signInView.agreementText}
                values={{
                  termsOfUse: (
                    <Link
                      href={PageUrl.TERMS_OF_USE}
                      target="_blank"
                      rel="noopener noreferrer"
                      withUnderline
                      inherit
                    >
                      <FormattedMessage
                        {...translations.signInView.termsOfUse}
                      />
                    </Link>
                  ),
                  privacyPolicy: (
                    <Link
                      href={PageUrl.PRIVACY_POLICY}
                      target="_blank"
                      rel="noopener noreferrer"
                      withUnderline
                      inherit
                    >
                      <FormattedMessage
                        {...translations.signInView.privacyPolicy}
                      />
                    </Link>
                  ),
                }}
              />
            </Text>
            <FormConsumer<SignInFormValues>>
              {({ formState, watch }) => {
                const requiredFieldValues = watch(['email', 'password']);
                const areAllRequiredFieldsFilled =
                  requiredFieldValues.every(Boolean);

                return (
                  <AuthButton
                    type="submit"
                    disabled={!areAllRequiredFieldsFilled}
                    loading={
                      formState.isSubmitting ||
                      isCustomerDataLoading ||
                      isVerificationEmailResending
                    }
                    withSpinner
                  >
                    <FormattedMessage
                      {...translations.signInView.submitButtonText}
                    />
                  </AuthButton>
                );
              }}
            </FormConsumer>
          </Stack>
        </Form>
      </AuthFormContainer>
    </AuthPageContent>
  );
};
